import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BUSINESS_INFO } from '../../mocks/lisaMerkleData';

interface HeroSectionProps {
  badge?: string;
  title: string;
  titleItalic?: string;
  subtitle: string;
  description: string;
  bgImage: string;
  bgImageAlt?: string;
  ctaText: string;
  ctaLink: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  overlayOpacity?: number;
}

export default function HeroSection({
  badge,
  title,
  titleItalic,
  subtitle,
  description,
  bgImage,
  ctaText,
  ctaLink,
  secondaryCtaText,
  overlayOpacity = 0.42,
}: HeroSectionProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.querySelectorAll('.hero-reveal').forEach((el, i) => {
          setTimeout(() => {
            (el as HTMLElement).style.opacity = '1';
            (el as HTMLElement).style.transform = 'translateY(0)';
          }, i * 140);
        });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex flex-col overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-[12000ms] ease-out"
        style={{ backgroundImage: `url(${bgImage})`, transform: 'scale(1.04)' }}
      />

      {/* Multi-layer overlay for cinematic depth */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(
            135deg,
            rgba(0,0,0,${overlayOpacity + 0.12}) 0%,
            rgba(0,0,0,${overlayOpacity - 0.05}) 40%,
            rgba(0,0,0,${overlayOpacity + 0.08}) 100%
          )`
        }}
      />
      {/* Bottom vignette */}
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent" />
      {/* Left edge vignette */}
      <div className="absolute top-0 bottom-0 left-0 w-1/3 bg-gradient-to-r from-black/25 to-transparent" />

      {/* Decorative vertical text — right side */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 z-10 hidden xl:flex flex-col items-center gap-4 opacity-30">
        <div className="w-px h-16 bg-white/40" />
        <span
          className="text-white text-[9px] tracking-[0.35em] uppercase font-sans"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          Beverly Hills · Los Angeles · California
        </span>
        <div className="w-px h-16 bg-white/40" />
      </div>

      {/* Decorative corner mark — top left (below nav) */}
      <div className="absolute top-32 left-8 z-10 hidden lg:flex items-start gap-3 opacity-40">
        <div className="flex flex-col gap-1">
          <div className="w-6 h-px bg-stone" />
          <div className="w-3 h-px bg-stone/50" />
        </div>
      </div>

      {/* Main Content */}
      <div ref={contentRef} className="relative z-10 flex flex-col justify-end flex-1 max-w-[1400px] mx-auto px-8 w-full pb-20 pt-44">
        <div className="max-w-4xl">

          {badge && (
            <div
              className="hero-reveal flex items-center gap-3 mb-7"
              style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}
            >
              <div className="w-8 h-px bg-stone" />
              <span className="text-[10px] text-white/60 tracking-[0.28em] uppercase font-sans">
                {badge}
              </span>
            </div>
          )}

          <h1
            className="hero-reveal font-display font-light text-white leading-[1.02]"
            style={{
              fontSize: 'clamp(2.8rem, 7vw, 6.5rem)',
              opacity: 0,
              transform: 'translateY(20px)',
              transition: 'opacity 0.9s ease, transform 0.9s ease',
            }}
          >
            {title}
            {titleItalic && (
              <>
                <br />
                <em
                  className="font-display italic"
                  style={{ color: '#D6C9B4', fontWeight: 300 }}
                >
                  {titleItalic}
                </em>
              </>
            )}
          </h1>

          <div
            className="hero-reveal flex items-center gap-4 my-7"
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}
          >
            <div className="w-14 h-px bg-stone" />
            <span className="text-stone/70 text-[10px] tracking-[0.2em] uppercase font-sans">
              {subtitle}
            </span>
          </div>

          <p
            className="hero-reveal text-white/70 text-base md:text-lg leading-relaxed mb-10 max-w-xl font-sans font-light"
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}
          >
            {description}
          </p>

          <div
            className="hero-reveal flex flex-wrap items-center gap-4"
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}
          >
            <Link
              to={ctaLink}
              className="px-9 py-4 bg-cream text-charcoal text-[10px] tracking-[0.2em] uppercase font-medium font-sans rounded-full hover:bg-white hover:scale-[1.02] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {ctaText}
            </Link>
            {secondaryCtaText && (
              <a
                href={BUSINESS_INFO.phoneHref}
                className="flex items-center gap-2.5 px-9 py-4 border border-white/25 text-white text-[10px] tracking-[0.2em] uppercase font-medium font-sans rounded-full hover:bg-white/10 hover:border-white/40 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-stone text-sm" />
                {secondaryCtaText}
              </a>
            )}
          </div>

          {/* Recognition badges — inline bottom row */}
          <div
            className="hero-reveal flex items-center gap-5 mt-14 pt-6 border-t border-white/10"
            style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}
          >
            <span className="text-white/25 text-[10px] tracking-[0.2em] uppercase font-sans">As Seen In</span>
            <span className="text-white/50 font-display italic text-sm tracking-wide">Elle Décor</span>
            <div className="w-px h-3 bg-white/20" />
            <span className="text-white/50 font-display italic text-sm tracking-wide">Architectural Digest</span>
            <div className="w-px h-3 bg-white/20 hidden sm:block" />
            <span className="hidden sm:block text-white/35 text-[10px] font-sans tracking-wider">UCLA Trained</span>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-7 right-8 z-10 hidden lg:flex flex-col items-center gap-2 opacity-40">
        <div className="w-px h-10 bg-white/30 relative overflow-hidden">
          <div className="absolute top-0 w-full bg-stone/80 animate-[scrollDrop_1.8s_ease_infinite]" style={{ height: '40%' }} />
        </div>
        <span className="text-white text-[9px] tracking-[0.25em] uppercase font-sans">Scroll</span>
      </div>
    </section>
  );
}
